import {
  useAdminEnrollUserToCourseMutation,
  useAdminUnenrollUserFromCourseMutation,
  useLazyGetUserEnrolledCoursesQuery,
} from "@api/course";
import { useLazyGetUserQuery, useUpdateUserMutation } from "@api/user";
import CourseCard from "@components/CourseCard";
import Header from "@components/Header";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { ModalVariant, useModal } from "@components/Modal";
import { useSearchCoursesModal } from "@components/Modal/SearchCoursesModal";
import { ArrowRightEndOnRectangleIcon } from "@heroicons/react/16/solid";
import {
  AcademicCapIcon,
  ArrowRightStartOnRectangleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useToastEffect } from "@hooks/hooks";
import { CourseDTO } from "@onetutor/shared/dto";
import { PatchUserDTO, Role } from "@onetutor/shared/user";
import { ChangeEvent, FormEvent, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

const UserManagementPage = () => {
  const { t } = useTranslation(["userManagement", "commons"]);
  const { t: tToast } = useTranslation("toast");
  const { t: tCourse } = useTranslation("coursePage");
  const [userId, setUserId] = useState<string>();

  const userIdInputId = useId();
  const titleInputId = useId();
  const roleSelectId = useId();
  const firstNameInputId = useId();
  const lastNameInputId = useId();

  const { Modal: ConfirmAdminModal, openModal: openConfirmAdminModal } =
    useModal();

  const [
    triggerGetUser,
    {
      data: userData,
      isError: isErrorGetUser,
      error: errorGetUser,
      reset: resetGetUser,
    },
  ] = useLazyGetUserQuery();
  useToastEffect({
    isError: isErrorGetUser,
    error: errorGetUser,
  });

  const [
    updateUser,
    {
      isSuccess: isSuccessUpdateUser,
      isError: isErrorUpdateUser,
      reset: resetUpdateUser,
    },
  ] = useUpdateUserMutation();

  const [triggerGetUserCourses, { data: userCourses = [] }] =
    useLazyGetUserEnrolledCoursesQuery();

  const userCourseIds = userCourses.map((course) => course.id);

  const [
    triggerAdminEnrollUser,
    {
      isSuccess: isSuccessEnrollUser,
      isError: isErrorEnrollUser,
      error: errorEnrollUser,
    },
  ] = useAdminEnrollUserToCourseMutation();

  const [
    triggerAdminUnenrollUser,
    {
      isSuccess: isSuccessUnenrollUser,
      isError: isErrorUnenrollUser,
      error: errorUnenrollUser,
    },
  ] = useAdminUnenrollUserFromCourseMutation();

  useToastEffect({
    isSuccess: isSuccessEnrollUser,
    isError: isErrorEnrollUser,
    error: errorEnrollUser,
    successMessage: tToast("userManagementPage.successEnrollUser"),
  });

  useToastEffect({
    isSuccess: isSuccessUnenrollUser,
    isError: isErrorUnenrollUser,
    error: errorUnenrollUser,
    successMessage: tToast("userManagementPage.successUnenrollUser"),
  });

  useToastEffect({
    isSuccess: isSuccessUpdateUser,
    isError: isErrorUpdateUser,
    successMessage: tToast("userManagementPage.successUpdateUser"),
  });

  function handleEnrollUser(courseId: string) {
    if (!userId) {
      return;
    }

    triggerAdminEnrollUser({ courseId, userId });
  }

  const { SearchCoursesModal, openModal: openCourseSearchModal } =
    useSearchCoursesModal({
      title: t("enrollUserInCourses"),
      excludeCourseIds: userCourseIds,
      children: (course) => (
        <CourseCard course={course} showUniversityBadge={true}>
          <IconWithTooltip
            icon={
              <ArrowRightEndOnRectangleIcon className="h-7 w-7 text-success" />
            }
            tooltip={tCourse("enroll")}
            handler={() => handleEnrollUser(course.id)}
            className="btn btn-ghost btn-circle"
          />
        </CourseCard>
      ),
    });

  const [user, setUser] = useState<PatchUserDTO>({});

  function handleRemoveCourse(course: CourseDTO) {
    if (!userId) {
      return;
    }

    triggerAdminUnenrollUser({ courseId: course.id, userId });
  }

  useEffect(() => {
    if (!userData) {
      return;
    }
    setUser({
      title: userData.title,
      firstName: userData.firstName,
      lastName: userData.lastName,
      role: userData.role,
    });
  }, [userData]);

  useEffect(() => {
    if (!isSuccessUpdateUser) {
      return;
    }

    resetUpdateUser();
    resetGetUser();
    setUserId(undefined);
    setUser({});
  }, [isSuccessUpdateUser, resetUpdateUser, resetGetUser]);

  const handleSearch = () => {
    if (!userId) {
      return;
    }
    triggerGetUser(userId);
    triggerGetUserCourses(userId);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!userId) {
      return;
    }
    const patchUser: PatchUserDTO = {
      title: user.title === "" ? null : user.title,
      firstName: user.firstName?.trim(),
      lastName: user.lastName?.trim(),
      role: user.role,
    };
    updateUser({
      userId,
      patchUser,
    });
  };

  const handleSelectRole = (e: ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value as Role;
    if (role === "admin") {
      openConfirmAdminModal();
      return;
    }
    setUser((prev) => ({
      ...prev,
      role,
    }));
  };

  return (
    <Header breadcrumbs={[{ name: t("userManagement") }]}>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <UserIcon className="mr-2 h-8 w-8" />
          <h1 className="font-bold text-2xl">{t("userManagement")}</h1>
        </div>
      </div>

      <div className="card mb-8 bg-neutral shadow-sm">
        <div className="card-body">
          <div className="form-control w-full max-w-2xl">
            <div className="mb-4">
              <label htmlFor={userIdInputId}>
                <span className="font-medium">User ID</span>
              </label>
              <div className="flex gap-4">
                <input
                  id={userIdInputId}
                  type="text"
                  className="input flex-1"
                  value={userId || ""}
                  onChange={(e) => {
                    setUserId(e.target.value);
                  }}
                  placeholder="Enter user UUID"
                />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleSearch}
                  disabled={!userId}
                >
                  {t("search", { ns: "commons" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {userData && (
        <div className="card bg-base-200 shadow-lg">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor={titleInputId}>
                    <span className="font-medium">{t("title")}</span>
                  </label>
                  <input
                    id={titleInputId}
                    type="text"
                    className="input w-full"
                    name="title"
                    value={user.title || ""}
                    onChange={(e) => {
                      setUser((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }));
                    }}
                    placeholder="Prof. Dr."
                  />
                </div>
                <div>
                  <label htmlFor={roleSelectId}>
                    <span className="font-medium">{t("role")}</span>
                  </label>
                  <select
                    id={roleSelectId}
                    className="select select-bordered w-full"
                    value={user.role}
                    onChange={handleSelectRole}
                  >
                    <option value="">Select role</option>
                    <option value="student">Student</option>
                    <option value="lecturer">Lecturer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label htmlFor={firstNameInputId}>
                    <span className="font-medium">{t("firstName")}</span>
                  </label>
                  <input
                    id={firstNameInputId}
                    type="text"
                    className="input w-full"
                    name="firstName"
                    value={user.firstName || ""}
                    onChange={(e) => {
                      setUser((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }));
                    }}
                    placeholder="Max"
                  />
                </div>
                <div>
                  <label htmlFor={lastNameInputId}>
                    <span className="font-medium">{t("lastName")}</span>
                  </label>
                  <input
                    id={lastNameInputId}
                    type="text"
                    className="input w-full"
                    name="lastName"
                    value={user.lastName || ""}
                    onChange={(e) => {
                      setUser((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }));
                    }}
                    placeholder="Mustermann"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button type="submit" className="btn btn-primary w-full">
                  {t("updateUser")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {userData && userCourseIds && (
        <div className="card mt-8 bg-base-200 shadow-lg">
          <div className="card-body">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <AcademicCapIcon className="mr-2 h-6 w-6" />
                <h2 className="font-bold text-xl">{t("enrolledCourses")}</h2>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={openCourseSearchModal}
              >
                {t("manageCourses")}
              </button>
            </div>

            {userCourses.length > 0 ? (
              <div className="flex flex-col space-y-2">
                {userCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    showUniversityBadge={true}
                  >
                    <IconWithTooltip
                      icon={
                        <ArrowRightStartOnRectangleIcon className="h-7 w-7 text-error" />
                      }
                      tooltip={tCourse("leave")}
                      handler={() => handleRemoveCourse(course)}
                      className="btn btn-ghost btn-circle"
                    />
                  </CourseCard>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm opacity-70">
                {t("noCoursesEnrolled")}
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmAdminModal
        title={t("confirmAdminRoleTitle")}
        onConfirm={() => {
          setUser((prev) => ({
            ...prev,
            role: "admin",
          }));
        }}
        onCancel={() => {}}
        confirmVariant={ModalVariant.DESTRUCTIVE}
      >
        {t("confirmAdminRoleMessage")}
      </ConfirmAdminModal>
      <SearchCoursesModal />
    </Header>
  );
};

export default UserManagementPage;
